import React from 'react';
import './StorefrontEditor.css';
import { Callout, CommandBar } from '@fluentui/react';

interface IStorefrontTextEditorProps {
    value?: string;
    notSetValue?: string;
    onChange: (value: string) => void;
    onEditModeChange?: (editMode: boolean) => void;
    style?: React.CSSProperties;
    /**
     * Overrides applied only while the placeholder (notSetValue) is showing. The placeholder is
     * editor chrome sitting on merchant-configured colors, so a caller that knows what it sits on
     * can hand it a color that contrasts — `style`'s color is the CONTENT color, which a merchant is
     * free to set to the background color itself.
     */
    placeholderStyle?: React.CSSProperties;
    editable?: boolean;
    className?: string;
    rootClassName?: string;
}

const StorefrontTextEditor: React.FC<IStorefrontTextEditorProps> = (props) => {
    const [editMode, setEditMode] = React.useState(false);
    const [text, setText] = React.useState(props.value ?? '');
    const editorRef = React.useRef<HTMLSpanElement | HTMLDivElement>(null);
    const draftTextRef = React.useRef(text);

    React.useEffect(() => {
        setText(props.value ?? '');
    }, [props.value]);

    React.useEffect(() => {
        draftTextRef.current = text;
    }, [text]);

    React.useEffect(() => {
        if (!editMode || !editorRef.current) return;

        editorRef.current.focus();
        draftTextRef.current = text;

        // Put the caret at the end so typing feels natural on click-to-edit.
        const selection = window.getSelection();
        if (!selection) return;

        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }, [editMode]);

    const displayedText = text || props.notSetValue || 'Click to edit';
    const isPlaceholder = !text;

    const sharedTextStyle: React.CSSProperties = {
        ...props.style,
        display: 'inline-block',
        minWidth: '1.5ch',
        outline: 'none',
        borderRadius: 3,
        padding: '0 2px',
    };

    if (!props.editable) {
        if (isPlaceholder) return null;
        return <div className={`storefront-text-editor-noneditable-root ${props.rootClassName ?? ''}`}>
            <span className={`storefront-text-editor-noneditable ${props.className ?? ''}`} style={sharedTextStyle}>{text}</span>
        </div>
    }

    return (
        <div className={`storefront-text-editor-root ${props.rootClassName ?? ''}`}>
            {editMode ? (
                <>
                {/* <Callout
                    isBeakVisible={false}
                    gapSpace={5}
                    target={editorRef}
                    >
                        <CommandBar
                            items={[
                                {
                                    key: 'Font',
                                    text: 'Font',
                                    iconProps: { iconName: 'Font' },
                                }
                            ]}
                        />
                </Callout> */}
                    <span
                        className={`storefront-text-editor-active ${props.className ?? ''}`}
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        role='textbox'
                        aria-label='Edit text'
                        onBlur={() => {
                            const nextValue = draftTextRef.current;
                            setEditMode(false);
                            props.onEditModeChange?.(false);
                            setText(nextValue);
                            props.onChange(nextValue);
                        }}
                        onInput={(e) => {
                            draftTextRef.current = e.currentTarget.textContent ?? '';
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                e.currentTarget.blur();
                            }
                        }}
                        style={{
                            ...sharedTextStyle,
                            cursor: 'text',
                        }}
                    >
                        {text}
                    </span>
                </>
            ) : (
                <span
                    className={`storefront-text-editor ${props.className ?? ''}`}
                    onClick={() => {
                        setEditMode(true);
                        props.onEditModeChange?.(true);
                    }}
                    style={{
                        ...sharedTextStyle,
                        opacity: isPlaceholder ? 0.6 : 1,
                        cursor: 'text',
                        ...(isPlaceholder ? props.placeholderStyle : {}),
                    }}
                >
                    {displayedText}
                </span>
            )}
        </div>
    );
};

export default StorefrontTextEditor;